import React, { useState, useEffect } from "react";
import Select from "react-select";
import { fetchAirports } from "../services/airportService";
import airports from "../data/airports";
import airlines from "../data/airlines";
import flightNumbers from "../data/flight-numbers";
import TermsModal from "./TermsModal"; // Import the reusable modal component
import "./CalculatorForm.css";

const cabinOptions = [
  // { value: "all", label: "All" },
  { value: "economy", label: "Economy" },
  { value: "premiumEconomy", label: "Premium Economy" },
  { value: "business", label: "Business Class" },
  { value: "first", label: "First Class" }
];

const tripOptions = [
  { value: "One Way", label: "One Way" },
  { value: "Round Trip", label: "Round Trip" }
];

function CalculatorForm({
  departure, setDeparture,
  arrival, setArrival,
  passengers, setPassengers,
  cabinClass, setCabinClass,
  tripType, setTripType,
  operatingCarrierCode, setOperatingCarrierCode,
  flightNumber, setFlightNumber,
  departureDate, setDepartureDate,
  email, setEmail, // Added email and setEmail props
  handleCalculate,
  isDisabled = false,
  isTermsAccepted, // Accept the status as a prop
  setTermsAccepted, // Function to update the status in the parent
}) {
  const [airportOptions, setAirportOptions] = useState(airports);
  const [carrierOptions, setCarrierOptions] = useState(airlines);
  const [flightNumberOptions, setFlightNumberOptions] = useState(flightNumbers);
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal visibility state

  // useEffect(() => {
  //   async function loadAirports() {
  //     const airports = await fetchAirports();
  //     setAirportOptions(airports);
  //   }
  //   loadAirports();
  // }, []);

  const handlePassengerChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 1 && value <= 100) {
      setPassengers(value);
    }else{
      setPassengers("");
    }
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    const today = new Date();
    const oneYearFromToday = new Date();
    oneYearFromToday.setFullYear(today.getFullYear() + 1);

    if (new Date(selectedDate) >= today && new Date(selectedDate) <= oneYearFromToday) {
      setDepartureDate(selectedDate);
    } else {
      alert("Please select a date between today and one year from now.");
    }
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleTermsAccept = () => {
    setTermsAccepted(true); // Update the status in the parent
    closeModal();
  };

  const handleTermsClick = () => {
    if (!isTermsAccepted) {
      openModal();
    } else {
      setTermsAccepted(false); // Toggle the status in the parent
    }
  };

  const handleCalculateClick = () => {
    if (!isTermsAccepted) {
      openModal();
    } else {
      handleCalculate();
    }
  };

  return (
    <div className="container mt-5 p-4 shadow-lg bg-light rounded" style={{ maxWidth: "1000px" }}>
      {/* Header Section */}
      <header className="text-center mb-4">
      <h2 className="text-primary fw-bold">Flight Emissions Calculator - Enter Details Below</h2>    

      <div className="warning-box">
    <strong>Note:</strong> In this beta version, the carbon footprint calculation is limited to consecutive travel segments. If your journey includes a transit stop, please calculate each segment separately.
    <br />
    For example, if you are traveling from Airport A to Airport C with a layover at Airport B, perform two calculations:
    <br />
    1) Airport A → Airport B
    <br />
    2) Airport B → Airport C
  </div>


      </header>

      {/* Departure Airport */}
      <div className="mb-3">
        <label className="form-label fw-bold">Departure Airport:</label>
        <Select 
          isDisabled={isDisabled}
          value={airportOptions.find(option => option.value === departure) || null}
          options={airportOptions} 
          onChange={option => setDeparture(option.value)}
          isSearchable
          placeholder="Select departure airport..."
        />
      </div>

      {/* Arrival Airport */}
      <div className="mb-3">
        <label className="form-label fw-bold">Arrival Airport:</label>
        <Select 
         isDisabled={isDisabled}
          value={airportOptions.find(option => option.value === arrival) || null}
          options={airportOptions} 
          onChange={option => setArrival(option.value)}
          isSearchable
          placeholder="Select arrival airport..."
        />
      </div>

      {/* Row for Operating Carrier Code, Flight Number, and Departure Date */}
      {/* <div className="row mb-3">
        <div className="col-md-4">
          <label className="form-label fw-bold">Operating Carrier Code:</label>
          <Select
            options={carrierOptions}            
            onChange={option => setOperatingCarrierCode(option.value)}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-bold">Flight Number:</label>
          <Select     
            options={flightNumberOptions}                
            onChange={option => setFlightNumber(option.value)}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-bold">Departure Date:</label>
          <input
            type="date"
            className="form-control"
            value={departureDate}
            onChange={handleDateChange}
          />
        </div>
      </div> */}

      {/* Row for Number of Passengers, Cabin Class, and Trip Type */}
      <div className="row mb-3">
        <div className="col-md-4">
          <label className="form-label fw-bold">Number of Passengers:</label>
          <input        
            disabled={isDisabled}     
            type="number" 
            className="form-control" 
            value={passengers} 
            onChange={handlePassengerChange}
            min="1" 
            max="100"
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-bold">Cabin Class:</label>
          <Select 
            isDisabled={isDisabled}
            value={cabinOptions.find(option => option.value === cabinClass) || null}
            options={cabinOptions} 
            onChange={option => setCabinClass(option.value)}
            defaultValue={cabinOptions[0]}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-bold">Trip Type:</label>
          <Select 
            isDisabled={isDisabled}
            value={tripOptions.find(option => option.value === tripType) || null}
            options={tripOptions} 
            onChange={option => setTripType(option.value)}
            defaultValue={tripOptions[0]}
          />
        </div>
      </div>

      {/* Email Address */}
      <div className="mb-3">
        <label className="form-label fw-bold">Email Address:</label>
        <input
          disabled={isDisabled}
          type="email"
          className="form-control"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      {/* Terms and Conditions Checkbox */}      
      <div className="form-check mt-3 mb-3">
        <input
          disabled={isDisabled}
          type="checkbox"
          className="form-check-input"
          id="termsCheckbox"
          checked={isTermsAccepted}
          onChange={handleTermsClick}
        />
        <label className="form-check-label" htmlFor="termsCheckbox">
          I accept the terms and conditions
        </label>
      </div>    

      {/* Calculate Button */}
      <button className="btn btn-primary w-100 p-2 fw-bold shadow" 
        disabled={isDisabled}
        onClick={handleCalculateClick}>
        Calculate Emissions
      </button>  

      {/* Terms Modal */}
      <TermsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onAccept={handleTermsAccept}
      />
    </div>
  );
}

export default CalculatorForm;